import CardColumn from "components/CardColumn";
import { Checkbox } from "@alm/flow-ds/components";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getCheckedBoxes } from "data/app/appSelectors";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import TaskMenu from "./TaskMenu";

interface Props {
	unclaimed?: boolean;
	handleSelectAllCheckboxesClick?: () => void;
	tasksCount?: number;
	claimTask?: (id: string | string[]) => Promise<void>;
	loading?: boolean;
}

export default function TasksHeading({
	unclaimed,
	handleSelectAllCheckboxesClick,
	tasksCount,
	claimTask,
	loading,
}: Props) {
	const { t } = useTranslation();

	const checkboxes = useSelector(getCheckedBoxes);

	const disableOnLoad = loading ? "disabled" : "";

	/**
	 * Handle claiming multiple tasks
	 */
	const handleClaimTasks = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: `Claim tasks: ${checkboxes
				.join(", ")
				.replace(/,\s*$/, "")}`,
		});

		if (claimTask) {
			claimTask(checkboxes);
		}
	}, [claimTask, checkboxes]);

	return (
		<div
			className={`list-group-heading tasks__heading--${
				unclaimed ? "unclaimedTasks" : "myTasks"
			} ${disableOnLoad}`}>
			{unclaimed && handleSelectAllCheckboxesClick && (
				<CardColumn width="none">
					<Checkbox
						id={`selectAllCheckboxes`}
						checked={checkboxes.length === tasksCount}
						value={!!checkboxes.length as unknown as string}
						onChange={handleSelectAllCheckboxesClick}
						label=""
						ariaLabel={`Select all tasks`}
						className="tasks-checkbox"
						disabled={loading ? true : false}
					/>
				</CardColumn>
			)}
			<CardColumn width="xl">{t("main.tasks.headings.title")}</CardColumn>
			<CardColumn width="xl">
				{t("main.tasks.headings.activity")}
			</CardColumn>
			<CardColumn className="task-card__date__heading--start" width="sm">
				{t("main.tasks.headings.started")}
			</CardColumn>
			<CardColumn className="task-card__date__heading--due" width="sm">
				{t("main.tasks.headings.dueDate")}
			</CardColumn>
			<CardColumn width="xs">
				{t("main.tasks.headings.priority")}
			</CardColumn>
			{!unclaimed && (
				<CardColumn width="sm">
					{t("main.tasks.headings.startedBy")}
				</CardColumn>
			)}
			<CardColumn
				className={`task-card__menu ${
					checkboxes.length && unclaimed
						? ""
						: "task-card__menu-hidden"
				} ${disableOnLoad}`}
				width="xs">
				<TaskMenu
					isDisabled={!checkboxes.length}
					prioritySet={false}
					canBeReassigned={false}
					canBeClaimed={true}
					claimTask={handleClaimTasks}
					loading={loading}
				/>
			</CardColumn>

			{unclaimed && <CardColumn width="sm" />}
		</div>
	);
}
